import { Pencil } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { StyledFileInput } from "@/components/shared/StyledFileInput";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { QuoteIntakeAnticipatedDocument } from "@/services/quoteIntakeService";
import type { QuoteRequestAttachmentUpload } from "@/services/quoteRequestsService";
import {
  AnticipatedItemFields,
  ANTICIPATED_ACCEPT,
  anticipatedSectionCopy,
} from "./anticipated-item-fields";
import {
  MAX_ANTICIPATED_SLOTS,
  QUOTE_REQUEST_MAX_ATTACHMENTS,
  fileSlotLabels,
  looseAttachmentsOf,
  setLooseSlot as setLooseSlotIn,
  setSlotFiles,
} from "./quote-attachment-slots";

export interface QuoteRequestReviewProps {
  title: string;
  onTitleChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  attachments: QuoteRequestAttachmentUpload[];
  onAttachmentsChange: (files: QuoteRequestAttachmentUpload[]) => void;
  anticipatedDocuments?: QuoteIntakeAnticipatedDocument[];
  /**
   * What the consumer has typed into the text-kind items so far, keyed by the
   * item's label. Held by the wizard rather than here so it survives stepping
   * back and forth between the review and the conversation.
   */
  intakeAnswers?: Record<string, string>;
  onIntakeAnswersChange?: (answers: Record<string, string>) => void;
  /** Professions this will be sent to — two when the job spans both. */
  disciplines?: Array<"notary" | "accountant">;
}

/**
 * What the conversation produced, for the consumer to check before it goes out.
 *
 * This was a form, and that was the wrong shape: nothing on this screen was
 * typed by the consumer. Presenting the assistant's words inside input boxes
 * says "fill this in" when the job is "read this and confirm it", and a
 * textarea sized for typing left a two-line summary floating in dead space,
 * making the most important text on the page look like the least important.
 *
 * So it reads as a summary and edits on request. The editing controls are the
 * same fields as before — the point is not that editing got harder, it is that
 * reading got easier, and reading is what this step is for.
 */
export function QuoteRequestReview({
  title,
  onTitleChange,
  description,
  onDescriptionChange,
  attachments,
  onAttachmentsChange,
  anticipatedDocuments = [],
  intakeAnswers = {},
  onIntakeAnswersChange,
  disciplines = [],
}: QuoteRequestReviewProps): JSX.Element {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  // Capped by MAX_ANTICIPATED_SLOTS, not by the attachment cap — two
  // quantities that shared one constant until spec b08814a3.
  const namedSlots = anticipatedDocuments.slice(0, MAX_ANTICIPATED_SLOTS);
  // Only file slots can hold an attachment, so only their labels may claim
  // one. A text item's label here would let a typed answer's label swallow a
  // loose file that happened to share it.
  const namedLabels = fileSlotLabels(namedSlots);
  const looseAttachments = looseAttachmentsOf(attachments, namedLabels);

  const setAnswer = (label: string, value: string): void => {
    onIntakeAnswersChange?.({ ...intakeAnswers, [label]: value });
  };

  // The slot algebra lives in quote-attachment-slots.ts, shared with the form
  // step. It was duplicated here byte-for-byte, which is exactly the drift
  // AnticipatedItemFields exists to prevent one level up.
  const changeSlotFiles = (doc: QuoteIntakeAnticipatedDocument, files: File[]): void => {
    onAttachmentsChange(setSlotFiles(attachments, doc, files));
  };

  const setLooseSlot = (index: number, file: File | null): void => {
    onAttachmentsChange(setLooseSlotIn(attachments, namedLabels, index, file));
  };

  const remaining = Math.max(0, QUOTE_REQUEST_MAX_ATTACHMENTS - attachments.length);
  const looseSlotCount = looseAttachments.length + (remaining > 0 ? 1 : 0);
  const atMax = remaining === 0;

  const anticipatedCopy = anticipatedSectionCopy(namedSlots);

  return (
    <Card className="border-border/80 shadow-sm dark:border-border/80 dark:bg-secondary/35">
      <CardContent className="space-y-6 pt-6">
        {isEditing ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="quote-review-title" className="text-sm font-medium text-foreground">
                {t("requests.user.quoteRequestTitleLabel")}
              </Label>
              <Input
                id="quote-review-title"
                value={title}
                onChange={(e) => onTitleChange(e.target.value)}
                className="bg-card dark:bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="quote-review-description"
                className="text-sm font-medium text-foreground"
              >
                {t("requests.user.quoteRequestDescriptionLabel")}
              </Label>
              <Textarea
                id="quote-review-description"
                value={description}
                onChange={(e) => onDescriptionChange(e.target.value)}
                rows={6}
                className="bg-card dark:bg-background"
              />
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(false)}>
              {t("requests.user.quoteReviewDone")}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-lg font-semibold leading-snug text-foreground">{title}</h2>
              {/* One quiet control. The consumer's job here is to read; editing
                  is the exception, so it does not get a primary button. */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0 text-muted-foreground"
                onClick={() => setIsEditing(true)}
              >
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                {t("requests.user.quoteReviewEdit")}
              </Button>
            </div>
            {/* Prose, not a filled-in textarea. This is the text professionals
                price against, so it should read like a paragraph. */}
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
              {description}
            </p>
          </div>
        )}

        {disciplines.length > 0 ? (
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <p className="text-sm font-medium text-foreground">
              {/* Article form, lowercase: "a un contador y un escribano". The
                  article is inside the translation because English needs a/an
                  per word, which a template cannot decide. */}
              {t("requests.user.quoteReviewGoesTo", {
                disciplines: disciplines
                  .map((d) => t(`requests.user.quoteDisciplineArticle_${d}`))
                  .join(t("requests.user.quoteDisciplineJoin")),
              })}
            </p>
            {/* Said before submitting, not discovered when the second invoice
                arrives. Two professions means two quotes and two charges. */}
            {disciplines.length > 1 ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {t("requests.user.quoteReviewTwoDisciplinesHint")}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* THE SECOND REGION (spec b08814a3, D5).
            Everything above is the request as the professionals will read it;
            everything below is optional material that sharpens their quote.
            The two were one continuous stack separated only by vertical
            rhythm, so the screen read as a single list of things to fill in
            when only the second half is. Mirrors the form step exactly. */}
        <div className="space-y-4 border-t border-border pt-5">
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-foreground">
              {/* Same interpolation as the fields screen: the cap belongs to
                  the constant, not to the sentence. */}
              {t(
                namedSlots.length > 0
                  ? anticipatedCopy.labelKey
                  : "requests.user.quoteRequestAttachmentLabel",
                { max: QUOTE_REQUEST_MAX_ATTACHMENTS },
              )}
            </h3>
            {namedSlots.length > 0 ? (
              <p className="text-sm leading-relaxed text-muted-foreground">{t(anticipatedCopy.hintKey)}</p>
            ) : null}
          </div>

          {namedSlots.length > 0 && (
            <AnticipatedItemFields
              idPrefix="quote-review-doc"
              items={namedSlots}
              attachments={attachments}
              onSlotFilesChange={changeSlotFiles}
              answers={intakeAnswers}
              onAnswerChange={setAnswer}
              remainingAttachments={remaining}
            />
          )}

          <div className="space-y-2">
            {namedSlots.length > 0 && looseSlotCount > 0 ? (
              <Label className="block text-sm font-normal text-muted-foreground">
                {t("requests.user.quoteRequestOtherAttachmentLabel")}
              </Label>
            ) : null}
            {Array.from({ length: looseSlotCount }, (_, index) => (
              <StyledFileInput
                key={index}
                id={`quote-review-attachment-${index}`}
                accept={ANTICIPATED_ACCEPT}
                value={looseAttachments[index]?.file ?? null}
                onChange={(file) => setLooseSlot(index, file)}
              />
            ))}
            <p className="text-xs text-muted-foreground">
              {atMax
                ? t("requests.user.quoteRequestAttachmentMaxReached", {
                    max: QUOTE_REQUEST_MAX_ATTACHMENTS,
                  })
                : // `n`, not `count`: i18next reserves `count` as the plural
                  // selector, and with only a base key there is no _one/_other
                  // variant for it to resolve to.
                  t("requests.user.quoteRequestAttachmentCount", {
                    n: attachments.length,
                    max: QUOTE_REQUEST_MAX_ATTACHMENTS,
                  })}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
